'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, FileText } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// Law firms: an after-hours intake routed by case type to the right person
// (the page's routing: personal injury, family law, estate), answered inside
// the 5-minute window the page talks about, and the consultation booked.

const DESKS = [
  { key: 'pi', name: 'Attorney A', area: 'Personal injury' },
  { key: 'family', name: 'Attorney B', area: 'Family law' },
  { key: 'estate', name: 'Paralegal intake', area: 'Estate planning' },
]
const CASES = {
  pi: { client: 'Chris Morgan', summary: 'Rear-ended on I-35, neck pain since Tuesday' },
  family: { client: 'Alicia Grant', summary: 'Needs advice on a custody change' },
  estate: { client: 'Robert Hale', summary: 'Wants a will and power of attorney' },
}
// Simulated clock (seconds) at each step: received, routed, replied, booked.
const CLOCK = [0, 0, 40, 134, 134, 134]
const DELAYS = [500, 1100, 1300, 1200, 900]
const SLA = 300

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

export default function IntakeRouter() {
  const [ref, onScreen] = useOnScreen()
  const [kind, setKind] = useState('pi')
  const [run, setRun] = useState(0)
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${kind}-${run}` })
  const finished = step >= DELAYS.length
  const desk = DESKS.find((d) => d.key === kind)
  const c = CASES[kind]
  const clock = CLOCK[Math.min(step, CLOCK.length - 1)]
  const pick = (key) => { setKind(key); setRun((n) => n + 1) }
  const circumference = 2 * Math.PI * 34

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-law"
      title="Intake routed in minutes"
      label="Demo: a law firm intake routed by case type and answered within five minutes"
      tabs={DESKS.map((d) => ({ key: d.key, label: d.area }))}
      activeTab={kind}
      onTab={pick}
      onReplay={() => pick(kind)}
    >
      <div className="svd-law-top">
        <div className="svd-intake">
          <span className="svd-intake-head"><FileText size={14} aria-hidden="true" /> New intake · web form · 9:41 PM</span>
          <strong>{c.client}</strong>
          <span>{desk.area}</span>
          <small>{c.summary}</small>
        </div>
        <div className="svd-sla" aria-label={`Response time ${fmt(clock)} of a 5-minute window`}>
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <circle cx="40" cy="40" r="34" className="svd-sla-bg" />
            <motion.circle
              cx="40" cy="40" r="34" className="svd-sla-fill"
              strokeDasharray={circumference}
              animate={{ strokeDashoffset: circumference * (1 - clock / SLA) }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
          </svg>
          <span><b>{fmt(clock)}</b><small>of 5:00</small></span>
        </div>
      </div>

      <div className="svd-desks">
        {DESKS.map((d) => {
          const target = d.key === kind
          return (
            <div key={d.key} className={`svd-desk${target && step >= 2 ? ' is-on' : ''}${!target && step >= 2 ? ' is-dim' : ''}`}>
              <span className="svd-desk-icon" aria-hidden="true"><Briefcase size={14} /></span>
              <strong>{d.name}</strong>
              <small>{d.area}</small>
              {target && step >= 2 ? (
                <motion.span className="svd-desk-new" initial={{ opacity: 0, scale: 0.6, y: -14 }} animate={{ opacity: 1, scale: 1, y: 0 }}>
                  New lead
                </motion.span>
              ) : null}
            </div>
          )
        })}
      </div>

      <Trail
        items={[
          { at: 1, text: 'Intake received after hours' },
          { at: 2, text: `Routed by case type to ${desk.name}` },
          { at: 3, text: `Client texted and emailed at ${fmt(134)}` },
          { at: 4, text: 'Consultation booked: tomorrow 10:00 AM' },
          { at: 5, text: 'Reminders and no-show recovery scheduled' },
        ]}
        step={step}
      />
      <ResultStamp show={finished} tone="good" text={`Responded in ${fmt(134)}`} id={`${kind}-${run}`} />
    </DemoFrame>
  )
}
